import { useState, useEffect, useRef } from "react";
import { User, Building2, Phone, MapPin, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

// ---- Walidatory (mirror backendu) ----
export function normPhonePL(p) {
  let d = String(p || "").replace(/[^0-9]/g, "");
  if (d.startsWith("48") && d.length === 11) d = d.slice(2);
  return d;
}
export function validPhonePL(p) {
  return normPhonePL(p).length === 9;
}
export function validPESEL(pesel) {
  pesel = String(pesel || "").trim();
  if (!/^\d{11}$/.test(pesel)) return false;
  const w = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
  const sum = w.reduce((a, v, i) => a + v * Number(pesel[i]), 0) % 10;
  return (10 - sum) % 10 === Number(pesel[10]);
}
export function validNIP(nip) {
  nip = String(nip || "").replace(/[^0-9]/g, "");
  if (nip.length !== 10) return false;
  const w = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const s = w.reduce((a, v, i) => a + v * Number(nip[i]), 0) % 11;
  return s !== 10 && s === Number(nip[9]);
}

// Buduje name + address z rozbitych pól (dla zgodności z resztą aplikacji / UKE).
// fallback = { name, address } — używane przy edycji starych rekordów, aby nie wyzerować danych.
export function composeClient(form, fallback = {}) {
  const ct = form.client_type || "osoba";
  const built = ct === "firma"
    ? String(form.company_name || "").trim()
    : `${String(form.first_name || "").trim()} ${String(form.last_name || "").trim()}`.trim();
  const name = built || String(fallback.name || "").trim();
  const streetNr = [form.street, form.house_no].filter((x) => x && String(x).trim()).join(" ").trim();
  const cityPart = [form.postal_code, form.city].filter((x) => x && String(x).trim()).join(" ").trim();
  let address = [streetNr, cityPart].filter(Boolean).join(", ");
  if (!address) address = String(fallback.address || "");
  return { name, address, teryt_nr: form.house_no || "" };
}

// Walidacja przed zapisem — zwraca komunikat błędu lub null.
// Przy edycji starego rekordu (legacy) pola nie są wymagane, sprawdzany jest tylko format podanych wartości.
export function validateClient(form, opts = {}) {
  if (opts.legacy) {
    if (form.phone && !validPhonePL(form.phone)) return "Numer telefonu musi mieć 9 cyfr.";
    if (form.pesel && !validPESEL(form.pesel)) return "Nieprawidłowy PESEL (błędna suma kontrolna).";
    if (form.nip && !validNIP(form.nip)) return "Nieprawidłowy NIP (błędna suma kontrolna).";
    return null;
  }
  const ct = form.client_type || "osoba";
  if (!validPhonePL(form.phone)) return "Podaj numer telefonu (9 cyfr).";
  if (ct === "firma") {
    if (!String(form.company_name || "").trim()) return "Podaj nazwę firmy.";
    if (!validNIP(form.nip)) return "Nieprawidłowy NIP (błędna suma kontrolna).";
  } else {
    if (!String(form.first_name || "").trim()) return "Podaj imię.";
    if (!String(form.last_name || "").trim()) return "Podaj nazwisko.";
    if (!validPESEL(form.pesel)) return "Nieprawidłowy PESEL (błędna suma kontrolna).";
  }
  if (!String(form.city || "").trim()) return "Wybierz miejscowość.";
  return null;
}

const inputCls = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500";

function Label({ children, required }) {
  return <label className="text-xs text-slate-400 mb-1 block">{children}{required && <span className="text-red-400"> *</span>}</label>;
}

// Autouzupełnianie ze słownika TERYT (miejscowość / ulica).
function TerytAutocomplete({ value, onText, onSelect, fetchOptions, disabled, freeText, placeholder, testid, display, emptyLabel }) {
  const [q, setQ] = useState(value || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  const boxRef = useRef();
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const handle = (v) => {
    setQ(v);
    onText(v);
    if (freeText) return;
    clearTimeout(timer.current);
    if (!v || v.trim().length < 2) { setOpts([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await fetchOptions(v.trim()); setOpts(r || []); setOpen(true); }
      catch { setOpts([]); }
      finally { setLoading(false); }
    }, 250);
  };
  return (
    <div className="relative" ref={boxRef}>
      <input value={q} disabled={disabled} placeholder={placeholder} data-testid={testid} autoComplete="off"
        onChange={(e) => handle(e.target.value)} onFocus={() => opts.length && setOpen(true)}
        className={inputCls + (disabled ? " opacity-50 cursor-not-allowed" : "")} />
      {loading && <span className="absolute right-2 top-2.5 text-[10px] text-slate-500">…</span>}
      {open && !freeText && (
        <div className="absolute z-[1200] left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-slate-900 border border-slate-600 rounded shadow-xl"
          data-testid={`${testid}-dropdown`}>
          {opts.length === 0 ? (
            <div className="px-2 py-2 text-xs text-slate-500">{emptyLabel}</div>
          ) : opts.map((o, i) => (
            <button type="button" key={i} data-testid={`${testid}-opt-${i}`}
              onClick={() => { const d = display(o); setQ(d); setOpen(false); onSelect(o, d); }}
              className="w-full text-left px-2 py-1.5 text-sm hover:bg-cyan-900/40 text-slate-200">
              {display(o)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClientIdentityFields({ form, set, setForm, initial, onTerytStatus, t = (x) => x }) {
  const [terytLoaded, setTerytLoaded] = useState(null); // null=ładowanie
  const [streetLocked, setStreetLocked] = useState(false);
  const ct = form.client_type || "osoba";

  useEffect(() => {
    api.get("/uke/teryt/status")
      .then((r) => { const on = (r.data?.simc?.count || 0) > 0; setTerytLoaded(on); onTerytStatus?.(on); })
      .catch(() => { setTerytLoaded(false); onTerytStatus?.(false); });
  }, []); // eslint-disable-line

  // Prefill pól rozbitych z name/address (stare rekordy oraz częściowo zmigrowane bez adresu).
  useEffect(() => {
    if (!initial?.id) return;
    const patch = {};
    if (!initial.first_name && !initial.company_name && initial.name) {
      const parts = String(initial.name).trim().split(/\s+/).filter(Boolean);
      patch.first_name = parts.length > 1 ? parts.slice(0, -1).join(" ") : String(initial.name).trim();
      patch.last_name = parts.length > 1 ? parts[parts.length - 1] : "";
    }
    if (!initial.street && !initial.city && initial.address) {
      const segs = String(initial.address).split(",").map((s) => s.trim()).filter(Boolean);
      if (segs[0]) {
        const sm = segs[0].match(/^(.*?)[\s,]+(\d+[A-Za-z]?(?:\/\d+)?)$/);
        if (sm) { patch.street = sm[1].trim(); patch.house_no = sm[2]; }
        else patch.street = segs[0];
      }
      if (segs[1]) {
        const m = segs[1].match(/(\d{2}-\d{3})\s*(.*)/);
        if (m) { patch.postal_code = m[1]; patch.city = m[2].trim(); }
        else patch.city = segs[1];
      }
    }
    if (Object.keys(patch).length) setForm((p) => ({ ...p, ...patch }));
  }, []); // eslint-disable-line

  const setType = (v) => setForm((p) => ({ ...p, client_type: v }));

  const suggestPostal = async (cityVal, streetVal, simcVal) => {
    try {
      const r = await api.get("/uke/teryt/postal", { params: { city: cityVal || "", street: streetVal || "", simc: simcVal || "" } });
      const pc = r.data?.postal_code;
      if (pc) setForm((p) => (String(p.postal_code || "").trim() ? p : { ...p, postal_code: pc, _postal_suggested: true }));
    } catch { /* brak podpowiedzi */ }
  };

  const onCitySelect = async (o) => {
    setForm((p) => ({ ...p, city: o.nazwa, teryt_simc: o.simc || "", teryt_terc: o.terc || "", street: "", teryt_ulic: "" }));
    try {
      const r = await api.get("/uke/teryt/search", { params: { type: "ulic", simc: o.simc, q: "" } });
      setStreetLocked((r.data || []).length === 0);
    } catch { setStreetLocked(false); }
    suggestPostal(o.nazwa, "", o.simc);
  };
  const onCityText = (v) => setForm((p) => ({ ...p, city: v, teryt_simc: "", teryt_terc: "", teryt_ulic: "" }));
  const onStreetSelect = (o, disp) => { setForm((p) => ({ ...p, street: disp, teryt_ulic: o.ulic || "" })); suggestPostal(form.city, disp, form.teryt_simc); };
  const onStreetText = (v) => setForm((p) => ({ ...p, street: v, teryt_ulic: "" }));

  const streetDisplay = (o) => `${String(o.cecha || "").trim()} ${String(o.nazwa || "").trim()}`.trim();
  const phoneDigits = String(form.phone || "").replace(/^\+?48/, "").replace(/\D/g, "").slice(0, 9);

  return (
    <div className="col-span-2 grid grid-cols-2 gap-3 border-b border-slate-700 pb-4 mb-1" data-testid="client-identity">
      {/* Typ klienta */}
      <div className="col-span-2 flex gap-2" data-testid="client-type-toggle">
        {[["osoba", t("Osoba fizyczna"), User], ["firma", t("Firma"), Building2]].map(([v, lbl, Ic]) => (
          <button type="button" key={v} data-testid={`client-type-${v}`} onClick={() => setType(v)}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded px-3 py-2 text-sm border transition-colors ${ct === v ? "bg-cyan-900/40 border-cyan-500 text-cyan-200" : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"}`}>
            <Ic size={15} /> {lbl}
          </button>
        ))}
      </div>

      {/* Dane tożsamości */}
      {ct === "firma" ? (
        <>
          <div className="col-span-2">
            <Label required>{t("Nazwa firmy")}</Label>
            <input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)}
              data-testid="client-company-name" className={inputCls} />
          </div>
          <div>
            <Label required>{t("NIP")}</Label>
            <input value={form.nip || ""} onChange={(e) => set("nip", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
              data-testid="client-nip" className={inputCls} placeholder={t("10 cyfr")} />
            {form.nip && !validNIP(form.nip) && <p className="text-[11px] text-red-400 mt-0.5">{t("Błędna suma kontrolna NIP")}</p>}
          </div>
        </>
      ) : (
        <>
          <div>
            <Label required>{t("Imię")}</Label>
            <input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)}
              data-testid="client-first-name" className={inputCls} />
          </div>
          <div>
            <Label required>{t("Nazwisko")}</Label>
            <input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)}
              data-testid="client-last-name" className={inputCls} />
          </div>
          <div>
            <Label required>{t("PESEL")}</Label>
            <input value={form.pesel || ""} onChange={(e) => set("pesel", e.target.value.replace(/[^0-9]/g, "").slice(0, 11))}
              data-testid="client-pesel" className={inputCls} placeholder={t("11 cyfr")} />
            {form.pesel && !validPESEL(form.pesel) && <p className="text-[11px] text-red-400 mt-0.5">{t("Błędna suma kontrolna PESEL")}</p>}
          </div>
          <div>
            <Label>{t("Nr dowodu osobistego")}</Label>
            <input value={form.id_number || ""} onChange={(e) => set("id_number", e.target.value.toUpperCase())}
              data-testid="client-id-number" className={inputCls} placeholder={t("np. ABC123456")} />
          </div>
        </>
      )}

      {/* Telefon */}
      <div className="col-span-2">
        <Label required><Phone size={11} className="inline mr-1 text-cyan-400" />{t("Telefon")}</Label>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-2 rounded bg-slate-800 border border-slate-700 text-slate-300 text-sm select-none">+48</span>
          <input value={phoneDigits} data-testid="client-phone"
            onChange={(e) => set("phone", "+48" + e.target.value.replace(/[^0-9]/g, "").slice(0, 9))}
            className={inputCls} placeholder="XXX XXX XXX" inputMode="numeric" />
        </div>
        {phoneDigits && phoneDigits.length !== 9 && <p className="text-[11px] text-red-400 mt-0.5">{t("Numer musi mieć 9 cyfr")}</p>}
      </div>

      {/* Adres — TERYT */}
      <div className="col-span-2 flex items-center gap-1.5 text-[11px] text-cyan-400 font-semibold uppercase tracking-wide mt-1">
        <MapPin size={12} /> {t("Adres (słownik TERYT)")}
      </div>
      {terytLoaded === false && (
        <div className="col-span-2 flex items-start gap-2 rounded bg-amber-900/20 border border-amber-800/40 text-amber-300 px-3 py-2 text-xs" data-testid="teryt-missing-banner">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>{t("Brak słownika TERYT — wpisz miejscowość/ulicę ręcznie. Aby włączyć autouzupełnianie, wgraj słownik GUS (PIT/UKE → TERYT).")}</span>
        </div>
      )}
      <div className="col-span-2">
        <Label required>{t("Miejscowość")}</Label>
        <TerytAutocomplete value={form.city} onText={onCityText} onSelect={onCitySelect}
          freeText={terytLoaded === false} testid="client-city" placeholder={t("Zacznij pisać…")}
          display={(o) => o.nazwa} emptyLabel={t("Brak w słowniku TERYT")}
          fetchOptions={(q) => api.get("/uke/teryt/search", { params: { type: "simc", q } }).then((r) => r.data)} />
      </div>
      <div>
        <Label>{t("Ulica")}</Label>
        <TerytAutocomplete value={form.street} onText={onStreetText} onSelect={onStreetSelect}
          freeText={terytLoaded === false}
          disabled={terytLoaded !== false && (streetLocked || !form.teryt_simc)}
          testid="client-street" placeholder={streetLocked ? t("Miejscowość bez ulic") : t("Zacznij pisać…")}
          display={streetDisplay} emptyLabel={t("Brak w słowniku TERYT")}
          fetchOptions={(q) => api.get("/uke/teryt/search", { params: { type: "ulic", simc: form.teryt_simc, q } }).then((r) => r.data)} />
        {terytLoaded !== false && !form.teryt_simc && <p className="text-[11px] text-slate-500 mt-0.5">{t("Najpierw wybierz miejscowość ze słownika")}</p>}
        {streetLocked && <p className="text-[11px] text-slate-500 mt-0.5" data-testid="street-locked-note">{t("Ta miejscowość nie ma ulic w słowniku")}</p>}
      </div>
      <div>
        <Label>{t("Nr budynku")}</Label>
        <input value={form.house_no || ""} onChange={(e) => set("house_no", e.target.value)}
          data-testid="client-house-no" className={inputCls} placeholder={t("np. 12A")} />
      </div>
      <div className="col-span-2">
        <Label>{t("Kod pocztowy")}</Label>
        <input value={form.postal_code || ""} data-testid="client-postal-code"
          onChange={(e) => { let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 5); if (v.length > 2) v = v.slice(0, 2) + "-" + v.slice(2); setForm((p) => ({ ...p, postal_code: v, _postal_suggested: false })); }}
          className={inputCls} placeholder="00-000" />
        {form._postal_suggested && <p className="text-[11px] text-cyan-500/80 mt-0.5" data-testid="postal-suggested-note">{t("Podpowiedziano z bazy — możesz zmienić")}</p>}
      </div>
    </div>
  );
}
